import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {ChangeDetectionStrategy, Component, computed, contentChild, ElementRef, input, Optional, signal} from "@angular/core";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {scrollDersomNødvendig} from "web-ng2/delt/dom/scrollIntoView";
import {betingetblinkInnAnimasjon} from "web-ng2/delt/animasjoner";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {farge_profil_200_trondheim} from "felles-ng2/fargepalett";

const debug = initDebugger('TittelVerdiKomponent');


@Component({
    selector: 'tittel',
    template: `
        <span class="tittel--kontainer">
            <ng-content></ng-content>
        </span>
        <span class="tittel--linje"></span>
        <ng-content select="tittel-handlinger-hoeyrestilt"></ng-content>
    `,
    imports: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TittelKomponent {
}


/** Brukes som barn av <tittel> i <verdi-tittel type="vertikal--med-linje">,
 * viser innholdet i høyre ende av linjen */
@Component({
    selector: 'tittel-handlinger-hoeyrestilt',
    template: `<ng-content></ng-content>`,
    imports: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TittelHandlingerHøyrestiltKomponent {
}


@Component({
    selector: 'verdi',
    template: `
        <ng-content></ng-content>`,
    host: {'data-testid': 'verdi'},
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VerdiKomponent {
}

/** Definerer et tittel-verdi-par.
 * Kan benyttes selvstendig eller som barn av {@link TittelVerdiSamlingKomponent}*/
@Component({
    selector: 'tittel-verdi',
    template: `
        @if (tittelElement()) {
            <ng-container>
                <ng-content select="tittel"></ng-content>
            </ng-container>
        } @else {
            <ng-container>
                <tittel>{{ tittelSomStreng() }}</tittel>
            </ng-container>
        }

        @if (verdiElement()) {
            <ng-container>
                <ng-content select="verdi"></ng-content>
            </ng-container>
        } @else {
            <ng-container>
                <verdi>{{ verdiSomStreng() }}</verdi>
            </ng-container>
        }
    `,
    styleUrl: 'tittel-verdi.scss',
    host: {
        '[@blinkInnBetinget]': 'blink()',
        '[class]': 'faktiskStil()',
        'data-testid': 'tittel-verdi'
    },
    animations: [betingetblinkInnAnimasjon({initialFargefarge: farge_profil_200_trondheim, blinkFarge: 'initial'})],
    imports: [
        TittelKomponent,
        VerdiKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TittelVerdiKomponent {
    tittelElement = contentChild(TittelKomponent);
    verdiElement = contentChild(VerdiKomponent);

    /** Angir tittel, alternativt angis tittel via `<tittel>`-komponenten */
    tittelSomStreng = input<string | null>(null, {alias: 'tittel'});
    /** Angir verdi, alternativt angis tittel via `<verdi>`-komponenten */
    verdiSomStreng = input<string | null>(null, {alias: 'verdi'});
    /** Angir visningsstil, dersom ikke angitt og vi er omsluttet av en <tittel-verdi-samling> komponent utledes stilen av {@link TittelVerdiSamlingKomponent#stil} */
    stil = input<TittelVerdiStil | null>(null);


    blink = signal(0)
    faktiskStil = computed(() => this.beregnStil());

    constructor(@Optional() public samling: TittelVerdiSamlingKomponent, private el: ElementRef<HTMLElement>) {
    }

    /** scroller til dette elementet, blinker det og setter så en permanent bakgrunnsfarge.
     * MERK kun eksplisitt støtte for vertikal--med-linje */
    fokuser(fokusert = true) {
        if (fokusert) {
            scrollDersomNødvendig(this.el.nativeElement);
            this.blink.update(i => i + 1);
            this.el.nativeElement.classList.add('fokusert');
        } else {
            this.el.nativeElement.classList.remove('fokusert');
        }
    }

    //private hjelpemetoder

    /** dersom pakket inn i <tittel-verdi-samling> lar vi stil satt på <tittel-verdi-samling>
     * diktere hvilken stil <tittel-verdi> skal ha */
    private beregnStil(): TittelVerdiStil {
        if (erIkkeNil(this.samling) && erNil(this.stil())) {
            switch (this.samling.stil()) {
                case  'vertikal':
                    return 'vertikal';
                case  'vertikal--med-linje':
                    return 'vertikal--med-linje';
                case  'horisontal':
                    return 'horisontal--en-linje';
                case  'horisontal--en-linje':
                    return 'horisontal';
                case  'horisontal--en-linje-spredd':
                    return 'horisontal';
                default:
                    return 'vertikal';
            }
        }

        return this.stil() ?? 'vertikal';
    }

}

export type TittelVerdiStil = 'vertikal' | 'vertikal--med-linje' | 'horisontal' | 'horisontal--en-linje'
